import {
  onNuxtReady,
  useAsyncData,
  useRoute,
  useRuntimeConfig,
  useState,
} from '#imports'
import { type Ref, type ShallowReactive, computed } from 'vue'

import type { SearchResponse } from '@algolia/client-search'
import type { AlgoliaProduct } from '@backmarket/http-api/src/api-specs-search-reco/search/searchAlgolia'
import type { SearchConfiguration } from '@backmarket/http-api/src/api-specs-search-reco/search/searchConfiguration'
import { useAlgoliaSearchOptions } from '@backmarket/nuxt-layer-algolia/useAlgoliaSearchOptions'
import { useTestChallenge } from '@backmarket/nuxt-layer-algolia/useRedirectToTestChallenge'
import { buildUiFilters } from '@backmarket/nuxt-layer-recommendation/filtering/buildUiFilters'
import { filtersToWhereClause } from '@backmarket/nuxt-layer-recommendation/filtering/filtersToWhereClause'
import { getGraphFacet } from '@backmarket/nuxt-layer-recommendation/filtering/getGraphFacet'
import {
  algoliaHitToProduct,
  algoliaHitToVariant,
} from '@backmarket/nuxt-layer-recommendation/helpers.ts'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import algoliasearch from 'algoliasearch'

import { useSwapEnabled } from '~/scopes/buyback/swap/composables/useSwapEnabled/useSwapEnabled'

import {
  COMING_SOON_HITS_PER_PAGE,
  COMING_SOON_INSERTION_INDEXES,
  HITS_PER_PAGE,
  HITS_PER_PAGE_WITH_BUYBACK,
} from '../../constants'
import { ROUTES as PRODUCT_LIST_ROUTES } from '../../route-names'

import type {
  ProductWithPosition,
  ProductsSearchData,
  SearchTrackingData,
} from './useProductsSearch.types'
import { buildSearchRequestOptions } from './useProductsSearch.utils'

const DEFAULT_DATA: ProductsSearchData = {
  products: [],
  total: 0,
  pageCount: 0,
  facets: [],
  priceFacet: undefined,
  offset: 0,
}

type VertexAiSearchResponse = SearchResponse<AlgoliaProduct> & {
  attributionToken?: string
  filter?: string
  orderBy?: string
  searchQuery?: string
}

export function useProductsSearch(
  identifier: string,
  configuration: SearchConfiguration,
  searchParams: {
    query?: Ref<string>
    filtersInput: ShallowReactive<Record<string, string[]>>
    sortInput: Ref<string>
    pageInput: Ref<number>
    priceInput: Ref<[number, number] | undefined>
    disjunctiveFacetKeys?: Ref<string[]>
    userToken?: string
    pageCategories?: string[]
  },
  options?: {
    paused?: Ref<boolean>
    feature?: string
  },
) {
  const searchTrackingData = useState<SearchTrackingData>(
    'search-query',
    () => ({
      id: '',
      index: '',
    }),
  )
  const algoliaSearchOption = useAlgoliaSearchOptions(
    'search',
    options?.feature,
  )
  const algoliaClient = algoliasearch('dummy_app_id', 'dummy_key', {
    ...algoliaSearchOption,
  })
  const logger = useLogger()
  const { redirectToTestChallengePage, isTestChallengeError } =
    useTestChallenge()
  const { isSwapEnabled } = useSwapEnabled()
  const { FF_PLP_COMING_SOON_PRODUCT_CARD = false } = useRuntimeConfig().public

  const route = useRoute()
  const isSearchResultsPage = route.name === PRODUCT_LIST_ROUTES.SEARCH_RESULTS

  // Search Configuration
  const index = computed(() => {
    return (
      [
        configuration.indexes.active,
        ...(configuration.indexes?.other ?? []),
      ].find(({ name }) => name === searchParams.sortInput.value) ??
      configuration.indexes.active
    )
  })

  const requestOptions = computed(() =>
    buildSearchRequestOptions({
      configuration,
      indexQueryParams: index.value.queryParams,
      filtersInput: searchParams.filtersInput,
      priceInput: searchParams.priceInput.value,
      pageInput: searchParams.pageInput.value,
      hitsPerPage: isSwapEnabled ? HITS_PER_PAGE_WITH_BUYBACK : HITS_PER_PAGE,
    }),
  )
  const experiments = useExperiments()
  const withFreshDataRefresh =
    experiments['experiment.plpDuplicatedAlgoliaCalls'] ===
    'withDuplicatedCalls'
  const withVariantLinks =
    experiments['experiment.algoliaCardLinks'] === 'withVariantLinks'
  const isComingSoonProductsEnabled = computed(
    () =>
      options?.feature === 'plp' &&
      FF_PLP_COMING_SOON_PRODUCT_CARD &&
      Boolean(configuration.indexes.coming_soon) &&
      searchParams.pageInput.value === 1 &&
      searchParams.priceInput.value === undefined &&
      searchParams.sortInput.value === configuration.indexes.active.name &&
      !Object.values(searchParams.filtersInput).some((values) => values.length),
  )

  const asyncData = useAsyncData(
    getAsyncDataKey('products-search', {
      identifier,
      index: index.value.name,
    }),
    async () => {
      if (options?.paused?.value) return null

      try {
        const queryValue = searchParams.query?.value ?? ''

        const disjunctiveKeys = (
          searchParams.disjunctiveFacetKeys?.value ?? []
        ).filter((key) => (searchParams.filtersInput[key]?.length ?? 0) > 0)
        const comingSoonIndex = isComingSoonProductsEnabled.value
          ? configuration.indexes.coming_soon
          : undefined

        const { results } = await algoliaClient.multipleQueries<AlgoliaProduct>(
          [
            {
              indexName: index.value.name,
              query: queryValue,
              params: {
                ...requestOptions.value,
                ...(searchParams.pageCategories && {
                  pageCategories: searchParams.pageCategories,
                }),
                ...(searchParams.userToken && {
                  userToken: searchParams.userToken,
                }),
              },
            },
            ...disjunctiveKeys.map((key) => {
              const { [key]: _excluded, ...filtersWithoutKey } =
                searchParams.filtersInput

              return {
                indexName: index.value.name,
                query: queryValue,
                params: {
                  ...requestOptions.value,
                  filters: filtersToWhereClause(
                    filtersWithoutKey,
                    configuration.complexFilter,
                  ),
                  facets: [key],
                  hitsPerPage: 0,
                  page: 0,
                },
              }
            }),
            ...(comingSoonIndex
              ? [
                  {
                    indexName: comingSoonIndex.name,
                    query: '',
                    params: {
                      ...comingSoonIndex.queryParams,
                      hitsPerPage: COMING_SOON_HITS_PER_PAGE,
                      page: 0,
                      filters: configuration.complexFilter,
                    },
                  },
                ]
              : []),
          ],
        )
        const [result, ...additionalResults] =
          results as VertexAiSearchResponse[]
        const facetResults = additionalResults.slice(0, disjunctiveKeys.length)
        const comingSoonResult = additionalResults[disjunctiveKeys.length]

        if (disjunctiveKeys.length > 0) {
          result.facets = {
            ...result.facets,
            ...Object.fromEntries(
              disjunctiveKeys.map((key, i) => [
                key,
                facetResults[i].facets?.[key] ?? {},
              ]),
            ),
          }
        }

        logger.info('[S&R] PLP search query success', {
          endpointName: 'algoliaSearch',
          type: 'API_REQUEST_SUCCESS',
          searchProcessingTime: result.processingTimeMS,
          owners: ['bot-squad-search-and-recommendation-front'],
        })

        if (result.nbHits === 0) {
          logger.info('[S&R] PLP returned empty results', {
            type: 'EMPTY_SEARCH_RESULTS',
            owners: ['bot-squad-search-and-recommendation-front'],
          })
        }

        searchTrackingData.value = {
          id: result.queryID,
          index: index.value.name,
        }

        return { result, comingSoonResult }
      } catch (e) {
        logger.error('[S&R] PLP search query failed', {
          endpointName: 'algoliaSearch',
          type: 'API_REQUEST_FAIL',
          e,
          owners: ['bot-squad-search-and-recommendation-front'],
        })

        if (isTestChallengeError(e)) {
          redirectToTestChallengePage()
        }

        throw e
      }
    },
    {
      default: () => DEFAULT_DATA,
      transform: (result) => {
        if (!result) return DEFAULT_DATA

        const { result: primaryResult, comingSoonResult } = result

        const useGradeLink =
          withVariantLinks &&
          (isSearchResultsPage ||
            hasUnsupportedFilterAttribute(
              searchParams.filtersInput,
              configuration.complexFilter,
              requestOptions.value.numericFilters,
            ))

        const pageOffset =
          (searchParams.pageInput.value - 1) * primaryResult.hitsPerPage

        return {
          total: primaryResult.nbHits + (comingSoonResult?.hits.length ?? 0),
          pageCount: primaryResult.nbPages,
          products: mergeComingSoonProducts(
            getProductsWithPosition(
              primaryResult.hits,
              configuration.productGroupingType,
              pageOffset,
              useGradeLink,
            ),
            comingSoonResult
              ? getProductsWithPosition(
                  comingSoonResult.hits,
                  configuration.productGroupingType,
                  0,
                  false,
                )
              : [],
          ),
          facets: buildUiFilters(
            [],
            configuration.facets,
            primaryResult.facets,
          ),
          priceFacet:
            configuration.priceFacet &&
            primaryResult.facets &&
            getGraphFacet(configuration.priceFacet, primaryResult.facets, {
              avg: primaryResult.facets_stats?.price.avg ?? 0,
              max: primaryResult.facets_stats?.price.max ?? 0,
            }),
          attributionToken: primaryResult.attributionToken,
          filter: primaryResult.filter,
          orderBy: primaryResult.orderBy,
          offset: pageOffset,
          searchQuery: primaryResult.searchQuery,
        }
      },
      lazy: false,
      watch: [() => index.value.name, requestOptions],
    },
  )

  if (import.meta.client && withFreshDataRefresh) {
    const { refresh } = asyncData
    onNuxtReady(() => {
      refresh()
    })
  }

  return asyncData
}

export function getProductsWithPosition(
  hits: AlgoliaProduct[],
  productGroupingType: SearchConfiguration['productGroupingType'],
  positionOffset: number,
  useGradeLink: boolean,
): ProductWithPosition[] {
  return hits.map((hit, index) => ({
    ...(productGroupingType === 'parent'
      ? algoliaHitToProduct(hit, { useGradeLink })
      : algoliaHitToVariant(hit)),
    position: positionOffset + index + 1,
  }))
}

export function mergeComingSoonProducts(
  products: ProductWithPosition[],
  comingSoonProducts: ProductWithPosition[],
): ProductWithPosition[] {
  if (!comingSoonProducts.length) return products

  return products.reduce((mergedProducts, product, index) => {
    const comingSoonProductIndex = COMING_SOON_INSERTION_INDEXES.indexOf(index)
    const comingSoonProduct =
      comingSoonProductIndex === -1
        ? undefined
        : comingSoonProducts[
            Math.min(comingSoonProductIndex, comingSoonProducts.length - 1)
          ]

    if (comingSoonProduct) {
      mergedProducts.push({
        ...comingSoonProduct,
        isComingSoon: true,
        comingSoonVariant:
          comingSoonProductIndex === 0 ? 'primary' : 'secondary',
      })
    }

    mergedProducts.push(product)

    return mergedProducts
  }, [] as ProductWithPosition[])
}

const PARENT_GROUPING_ATTRIBUTES = new Set([
  'cat_id',
  'brand',
  'brand_clean',
  'model',
  'model_clean',
])

function hasUnsupportedFilterAttribute(
  filtersInput: Record<string, string[]>,
  complexFilter = '',
  numericFilters: string[] = [],
) {
  const hasUnsupportedFacet = Object.entries(filtersInput).some(
    ([attribute, values]) =>
      values.length > 0 && !PARENT_GROUPING_ATTRIBUTES.has(attribute),
  )

  if (hasUnsupportedFacet) return true

  const filterExpressions = [complexFilter, ...numericFilters]

  return filterExpressions.some((filter) => {
    // A full Algolia filter parser would be more precise but harder to read.
    // PLP filters follow this grammar, so collecting field prefixes is enough.
    return Array.from(
      filter.matchAll(
        /(?:^|\b(?:AND|OR|NOT)\b|\()\s*([A-Za-z_][\w.-]*)\s*(?=:|<=|>=|!=|=|<|>)/g,
      ),
      (match) => match[1],
    ).some((attribute) => !PARENT_GROUPING_ATTRIBUTES.has(attribute))
  })
}
